import React, { useEffect, useState } from 'react';
import { MANUAL_TYPE_OPTIONS } from '../utils/allocationMath';

const OTHER = '__other__';

// Modal for a fully manual component (Aaron 2026-09-10): name + optional code
// + MANDATORY asset class (only the broad classes the AP List actually uses)
// + a Type that is one of the builder's existing types or free text. It
// replaces the earlier inline "+ Add manually…" option inside the fund picker.
// Session-only — nothing is persisted; the result is flagged is_custom so
// every output marks it as adviser-classified. The Type label is builder-only.
const ManualEntryModal = ({ initial, assetClassOptions, onSave, onCancel }) => {
  const init = initial || {};
  const presetType = MANUAL_TYPE_OPTIONS.includes(init.type);
  const [name, setName] = useState(init.name || '');
  const [code, setCode] = useState(init.code || '');
  const [acg, setAcg] = useState(init.acg || '');
  const [typeSel, setTypeSel] = useState(init.type ? (presetType ? init.type : OTHER) : 'Fund');
  const [typeText, setTypeText] = useState(presetType ? '' : (init.type || ''));

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const type = typeSel === OTHER ? typeText.trim() : typeSel;
  const canSave = !!(name.trim() && acg && type);

  const save = () => {
    if (!canSave) return;
    onSave({
      fund_name: name.trim(),
      apir_code: code.trim().toUpperCase(),
      tr_code: '',
      asset_class_group: acg,
      is_custom: true,
      custom_type: type,
    });
  };

  return (
    <div
      className="alloc-modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="alloc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="alloc-modal-title"
        onKeyDown={(e) => { if (e.key === 'Enter' && canSave) save(); }}
      >
        <h5 id="alloc-modal-title">{initial ? 'Edit manual component' : 'Add manual component'}</h5>

        <label>
          Name
          <input
            className="form-control form-control-sm"
            autoFocus
            placeholder="e.g. Direct property, BHP shares"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label>
          Code (optional)
          <input
            className="form-control form-control-sm"
            placeholder="APIR / ASX code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
          />
        </label>

        <label>
          Asset class
          <select
            className="form-select form-select-sm"
            value={acg}
            onChange={(e) => setAcg(e.target.value)}
          >
            <option value="">Select an asset class…</option>
            {(assetClassOptions || []).map((ac) => <option key={ac} value={ac}>{ac}</option>)}
          </select>
        </label>

        <label>
          Type
          <select
            className="form-select form-select-sm"
            value={typeSel}
            onChange={(e) => setTypeSel(e.target.value)}
          >
            {MANUAL_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            <option value={OTHER}>Other…</option>
          </select>
        </label>

        {typeSel === OTHER && (
          <label>
            Type description
            <input
              className="form-control form-control-sm"
              autoFocus
              placeholder="e.g. Direct shares"
              value={typeText}
              onChange={(e) => setTypeText(e.target.value)}
            />
          </label>
        )}

        <div className="alloc-modal-actions">
          <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
          <button
            className="btn btn-primary btn-sm"
            disabled={!canSave}
            title={canSave ? '' : 'Enter a name, pick an asset class and a type'}
            onClick={save}
          >
            {initial ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManualEntryModal;
